import { useEffect, useState } from "react";

function slugify(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function buildToc(headings) {
  const root = [];
  const stack = [];
  headings.forEach((h) => {
    const level = Number(h.tagName.substring(1));
    const text = h.textContent || "";
    const item = { id: h.id, text, level, children: [] };

    while (stack.length && stack[stack.length - 1].level >= level) {
      stack.pop();
    }

    if (stack.length === 0) {
      root.push(item);
    } else {
      stack[stack.length - 1].children.push(item);
    }

    stack.push(item);
  });
  return root;
}

export default function HeadingToc({ containerRef, maxLevel = 3, color = "var(--black88)" }) {
  const [toc, setToc] = useState([]);

  useEffect(() => {
    if (!containerRef?.current) return;

    const el = containerRef.current;

    const ensureIds = (nodes) => {
      const idCounts = new Map();
      nodes.forEach((h) => {
        if (!h.id) {
          const base = slugify(h.textContent || "heading");
          const count = idCounts.get(base) || 0;
          const id = count === 0 ? base : `${base}-${count}`;
          idCounts.set(base, count + 1);
          h.id = id;
        }
      });
    };

    const refresh = () => {
      const selector = Array.from({ length: maxLevel }, (_, i) => `h${i + 1}`).join(",");
      const found = Array.from(el.querySelectorAll(selector));
      ensureIds(found);
      setToc(buildToc(found));
    };

    // Initial build
    refresh();

    // Observe changes to keep TOC in sync with loaded content
    const observer = new MutationObserver(() => refresh());
    observer.observe(el, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [containerRef, maxLevel]);

  const handleClick = (e, id) => {
    e.preventDefault();
    const target = document.getElementById(id);
    if (target) {
      const navHeight = 72; // Fixed nav height
      const offset = 24; // Additional padding
      const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - navHeight - offset;
      window.scrollTo({ top: targetPosition, behavior: "smooth" });
    }
  };

  const renderList = (items, depth = 1) => (
    <ul style = { { margin : 0, padding : 0, textDecoration : "none", listStyle : "none", display : "flex", flexDirection : "column", gap : depth === 1 ? "4px" : "0px", width : "100%" } }>
      { items.map((item) => (
        <li
          key = { item.id }
          style = { {
            margin : 0,
            padding : 0,
            width : "100%",
            // Keep top-level items spaced out even when there are few
            marginBottom : depth === 1 ? "4px" : "0px",
          } }
        >
          <a
            href={`#${item.id}`}
            onClick={(e) => handleClick(e, item.id)}
            style = { {
              display : "block",
              width : "100%",
              background : color,
              color : "white",
              fontSize : "var(--font-size-tiny)",
              lineHeight : "140%",
              padding : "1px 6px",
              cursor : "pointer",
              textDecoration : "none",
              fontFamily : "Open Sans, sans-serif",
            } }
          >
            { item.text }
          </a>
          { item.children && item.children.length > 0 && (
            <div style = { { display : "flex", flexDirection : "column", gap : "0px", width : "100%",
                paddingLeft : "40px" // indentation
        } }>{ renderList(item.children, depth + 1) }</div>
          )}
        </li>
      ))}
    </ul>
  );

  if (!toc.length) return null;

  return (
    <div
      aria-label="Table of contents"
      className = "toc"
      style={{
        margin : "80px 0px",
      }}
    >
        <p style = { { fontSize : "var(--font-size-tiny)", color : color } }>Table of Contents</p>
        { renderList(toc) }
    </div>
  );
}
